import React, { useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useSearchCommonPageData } from '@/app/components/search/searchCommonPageProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import { useCanShowModal } from '@/app/components/search/hooks/useCanShowModal.ts';
import {
  APEX_FLEXIBLE_DAYS_EXACT,
  type ApexFlexibleDays,
  parseApexFlexibleDays,
} from '@/app/common/apex/apexFlexibleDays.ts';
import { calculateNights } from '@/app/components/search/utils/buildDealCards/utils.ts';
import { Icon } from '@/app/common/icons/Icon';
import getSearchParams, { getSearchParamsClient } from '../../../common/getSearchParams';
import { removeEmptyParams, removeSearchParams } from '../../Filters/utils';
import { getFlexibilityOptions } from './utils.ts';
import {
  CollapsedRow,
  CollapsedRowChevron,
  CollapsedRowLabel,
  CollapsedRowValue,
  SwitcherCaption,
  SwitcherContainer,
  OptionPill,
  SwitcherIcon,
  SwitcherInfo,
  SwitcherOptions,
  SwitcherTitle,
} from './styled.ts';

const FlexibleDatesSheet = dynamic(() => import('./FlexibleDatesSheet.tsx'), { ssr: false });

/**
 * MPH-945: lets the user widen the departure window by ±1, ±3 or ±7 days without going back to
 * search. Only the departure date moves — the trip length stays as searched — and nothing is
 * applied until the user picks an option.
 *
 * Desktop applies on click, since the results are right there to see. Mobile confirms in a bottom
 * sheet, since the results sit behind it.
 */
const FlexibleDatesSwitcher = () => {
  const { searchParams: searchParamsServer, localization } = usePageWrapperData();
  const { isMobile } = useCommonClient();
  const { handleFiltersChanged } = useSearchCommonPageData();
  const { t, translateWithElements } = useTranslation();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // The sheet outlives its own close so OpenXModal can play the exit animation and keep its
  // ghost-click blocker up: iOS synthesises a click up to ~300ms after the tap, which would
  // otherwise land on the result card the sheet was covering.
  const { canShowModal: canShowSheet } = useCanShowModal(isSheetOpen);
  const pillRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Which pill the keyboard is on. Null means "follow the selection", which is where focus enters.
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const { lang, locale } = localization;
  const searchParams = getSearchParamsClient(searchParamsServer);
  const dateFrom = searchParams[URLParameters.DATE_FROM];
  const dateTo = searchParams[URLParameters.DATE_TO];
  const nights = calculateNights(dateFrom, dateTo);

  // The switcher describes a window around a departure date and a trip length, so it has nothing to
  // say without both. configValuesApp.apexFlexibleDatesEnabled already rules out searches with no
  // fixed dates; this is the last-line guard for a malformed URL.
  if (!dateFrom || !dateTo || !(nights > 0)) return null;

  const selectedDays = parseApexFlexibleDays(searchParams[URLParameters.APEX_FLEXIBLE_DAYS]);
  const options = getFlexibilityOptions({ dateFrom, selectedDays, language: lang, locale, t });
  const selectedOption = options.find((option) => option.selected);
  const selectedIndex = options.findIndex((option) => option.selected);
  const tabStopIndex = focusedIndex ?? (selectedIndex === -1 ? 0 : selectedIndex);

  const applyDays = (days: ApexFlexibleDays) => {
    setIsSheetOpen(false);
    if (days === selectedDays) return;

    const searchParamsAfterChange = getSearchParams();
    const searchParamsWithWindow = { ...searchParamsAfterChange };

    if (days === APEX_FLEXIBLE_DAYS_EXACT) delete searchParamsWithWindow[URLParameters.APEX_FLEXIBLE_DAYS];
    else searchParamsWithWindow[URLParameters.APEX_FLEXIBLE_DAYS] = `${days}`;
    // The results were priced for the previous window, so the search context must not be reused.
    delete searchParamsWithWindow[URLParameters.VC_SEARCH_ID];

    // The filter-reload path keeps the active filters and resets paging; the window itself travels in
    // searchParams, which is what lets "Exact dates" drop the parameter instead of blanking it.
    handleFiltersChanged({
      searchParams: searchParamsWithWindow,
      appliedFilters: removeEmptyParams(removeSearchParams(searchParamsAfterChange)),
    });
  };

  /**
   * Arrows move focus only; Space and Enter apply. Selection-follows-focus is the usual radio
   * pattern, but here every change is a full results reload, so traversing Exact dates → ±7 would
   * fire three searches the user never asked for.
   */
  const moveFocus = (event: React.KeyboardEvent, index: number) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;

    event.preventDefault();
    const next = (index + step + options.length) % options.length;
    setFocusedIndex(next);
    pillRefs.current[next]?.focus();
  };

  const caption =
    selectedDays === APEX_FLEXIBLE_DAYS_EXACT
      ? nights === 1
        ? t('vacation.oss.flexible_dates.subtitle_night')
        : t('vacation.oss.flexible_dates.subtitle_nights', { nights: `${nights}` })
      : translateWithElements(
          nights === 1 ? 'vacation.oss.flexible_dates.applied_night' : 'vacation.oss.flexible_dates.applied_nights',
          {
            dates: <b key="flexible-dates-window">{selectedOption?.windowLabel}</b>,
            nights: <span key="flexible-dates-nights">{nights}</span>,
          }
        );

  const calendarIcon = (
    <Icon category="Office and stationery" name="icon-calendar-range" size={20} color="iconPrimary" alt="" />
  );

  if (isMobile) {
    return (
      <>
        <CollapsedRow
          type="button"
          onClick={() => setIsSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={isSheetOpen}
        >
          <CollapsedRowLabel>
            {calendarIcon}
            {t('vacation.oss.flexible_dates.title')}
          </CollapsedRowLabel>
          <CollapsedRowValue>
            {selectedOption?.label}
            <CollapsedRowChevron />
          </CollapsedRowValue>
        </CollapsedRow>
        {canShowSheet && (
          <FlexibleDatesSheet
            isOpen={isSheetOpen}
            options={options}
            nights={nights}
            onClose={() => setIsSheetOpen(false)}
            onApply={applyDays}
          />
        )}
      </>
    );
  }

  return (
    <SwitcherContainer>
      <SwitcherInfo>
        <SwitcherIcon>{calendarIcon}</SwitcherIcon>
        <div>
          <SwitcherTitle>{t('vacation.oss.flexible_dates.title')}</SwitcherTitle>
          <SwitcherCaption>{caption}</SwitcherCaption>
        </div>
      </SwitcherInfo>
      <SwitcherOptions role="radiogroup" aria-label={t('vacation.oss.flexible_dates.title')}>
        {options.map((option, index) => (
          <OptionPill
            key={option.days}
            ref={(el: HTMLButtonElement | null) => {
              pillRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={option.selected}
            // A radio group is one tab stop: Tab reaches the current pill, arrows move between them.
            tabIndex={index === tabStopIndex ? 0 : -1}
            selected={option.selected}
            // Space and Enter fire click natively on a button, so this is the apply path for both
            // pointer and keyboard.
            onClick={() => applyDays(option.days)}
            onKeyDown={(event) => moveFocus(event, index)}
            onFocus={() => setFocusedIndex(index)}
            onBlur={() => setFocusedIndex(null)}
          >
            {option.label}
          </OptionPill>
        ))}
      </SwitcherOptions>
    </SwitcherContainer>
  );
};

export default FlexibleDatesSwitcher;
